import { Check, Copy } from "lucide-react";
import { useState } from "react";
import styled from "styled-components";

interface CopyableDetailRowProps {
  className?: string;
  label: string;
  value?: string | number | null;
  copyValue?: string;
}

export const CopyableDetailRow = styled(
  ({ className, label, value, copyValue }: CopyableDetailRowProps) => {
    const [copied, setCopied] = useState(false);

    const display =
      value === undefined || value === null || value === ""
        ? "None"
        : String(value);
    const toCopy = copyValue ?? (display === "None" ? "" : display);
    const canCopy = toCopy.length > 0;

    const handleCopy = (e: React.MouseEvent | React.KeyboardEvent) => {
      if (!canCopy) return;
      e.stopPropagation();
      navigator.clipboard
        .writeText(toCopy)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        })
        .catch(() => {
          // clipboard write failed (e.g., permissions denied); do not show success state
        });
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (!canCopy) return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleCopy(e);
      }
    };

    return (
      <div
        className={className}
        onClick={handleCopy}
        onKeyDown={handleKeyDown}
        role={canCopy ? "button" : undefined}
        tabIndex={canCopy ? 0 : undefined}
        data-can-copy={canCopy}
        title={canCopy ? `Click to copy: ${toCopy}` : undefined}
        aria-label={canCopy ? `Copy ${label}: ${toCopy}` : undefined}
      >
        <div className="detail-label">{label}</div>
        <div className="detail-value">{display}</div>
        {canCopy && (
          <div className="copy-icon" data-copied={copied}>
            {copied ? <Check size={11} /> : <Copy size={11} />}
          </div>
        )}
      </div>
    );
  }
)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
  font-size: 0.6rem;
  padding: 2px 4px;
  border-radius: 3px;

  &[data-can-copy="true"] {
    cursor: pointer;
    &:hover,
    &:focus-visible {
      background-color: ${({ theme }) => theme.palette.action.hover};
      .copy-icon {
        visibility: visible;
      }
    }
    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.palette.primary.main};
      outline-offset: 1px;
    }
  }

  .detail-label {
    min-width: 100px;
    font-weight: bold;
    color: ${({ theme }) => theme.palette.text.secondary};
  }

  .detail-value {
    flex: 1;
    min-width: 0;
    word-break: break-word;
  }

  .copy-icon {
    visibility: hidden;
    display: flex;
    align-items: center;
    color: ${({ theme }) => theme.palette.text.secondary};

    &[data-copied="true"] {
      visibility: visible;
      color: ${({ theme }) => theme.palette.success.main};
    }
  }
`;

export default CopyableDetailRow;
